import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '../components/shared/Button';
import { Stepper } from '../components/shared/Stepper';
import { VehicleBanner } from '../components/tire/VehicleBanner';
import { TireResultCard } from '../components/tire/TireResultCard';
import { useInspectionStore, determineOverallStatus, generateRecommendations, TIRE_BRANDS } from '../store/inspectionStore';
import { api } from '../lib/api';
import type { TireImage, TirePosition } from '../types';

// Validation schema for Step 1
const vehicleSchema = z.object({
  vinOrPlate: z.string().min(1, 'VIN or License Plate is required'),
});

type VehicleFormData = z.infer<typeof vehicleSchema>;

const steps = [
  { label: 'Vehicle', description: 'Identify vehicle' },
  { label: 'Photos', description: 'Upload tire images' },
  { label: 'Analyze', description: 'Process images' },
  { label: 'Summary', description: 'View results' },
];

export const InspectWizard = () => {
  const navigate = useNavigate();
  const {
    currentStep,
    vehicle,
    tireBrand,
    tireImages,
    tireResults,
    isAnalyzing,
    inspection,
    setVehicle,
    setTireBrand,
    addTireImage,
    removeTireImage,
    setTireResults,
    setIsAnalyzing,
    setInspection,
    resetWizard,
    nextStep,
    previousStep,
  } = useInspectionStore();

  const [isLoadingVehicle, setIsLoadingVehicle] = useState(false);
  const [vehicleError, setVehicleError] = useState<string | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [currentAnalysisStep, setCurrentAnalysisStep] = useState(0);
  const [isTireBrandExpanded, setIsTireBrandExpanded] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<VehicleFormData>({
    resolver: zodResolver(vehicleSchema),
  });

  // Reset wizard on mount
  useEffect(() => {
    resetWizard();
  }, [resetWizard]);

  // Progressive analysis steps animation
  const analysisSteps = [
    { label: 'Initializing ML models', icon: '🤖' },
    { label: 'Loading tire images', icon: '📸' },
    { label: 'Image preprocessing', icon: '🔄' },
    { label: 'Running CNN inference', icon: '🧠' },
    { label: 'Edge detection analysis', icon: '📐' },
    { label: 'Tread depth measurement', icon: '📏' },
    { label: 'Wear pattern classification', icon: '🔍' },
    { label: 'DOT code extraction (OCR)', icon: '🔤' },
    { label: 'Generating safety report', icon: '📊' },
  ];

  useEffect(() => {
    if (isAnalyzing && currentStep === 2) {
      // Reset to first step when analysis starts
      setCurrentAnalysisStep(0);

      // Progress through steps sequentially
      const stepInterval = setInterval(() => {
        setCurrentAnalysisStep((prev) => {
          if (prev < analysisSteps.length - 1) {
            return prev + 1;
          }
          return prev;
        });
      }, 500); // Each step shows for 500ms

      return () => clearInterval(stepInterval);
    }
  }, [isAnalyzing, currentStep]);

  // Smooth transition helper with tire rotation
  const transitionToNextStep = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      nextStep();
      setTimeout(() => {
        setIsTransitioning(false);
      }, 500);
    }, 2500); // 2.5 second tire rotation for professional feel
  };

  // Step 1: Vehicle identification
  const handleVehicleSubmit = async (data: VehicleFormData) => {
    setIsLoadingVehicle(true);
    setVehicleError(null);

    try {
      const input = data.vinOrPlate.trim().toUpperCase();
      const isVin = input.length > 10;

      const vehicleData = await api.resolveVehicle(
        isVin ? { vin: input } : { plate: input }
      );

      setVehicle(vehicleData);
      transitionToNextStep();
    } catch (error) {
      setVehicleError('Failed to resolve vehicle. Please try again.');
    } finally {
      setIsLoadingVehicle(false);
    }
  };

  // Step 2: Tire photo uploads
  const handleTireUpload = (position: TirePosition, file: File) => {
    const preview = URL.createObjectURL(file);
    const image: TireImage = {
      id: `${position}-${Date.now()}`,
      position,
      file,
      preview,
    };
    addTireImage(image);
  };

  const canProceedToAnalysis = tireImages.length >= 1;

  // Step 3: ML Analysis
  const handleAnalyze = async () => {
    // Show tire rotation transition before moving to analysis step
    setIsTransitioning(true);
    setTimeout(() => {
      setIsAnalyzing(true);
      nextStep();
      setIsTransitioning(false);

      // Start ML analysis
      (async () => {
        try {
          console.log('🤖 Starting ML-powered tire analysis...');
          console.log(`📊 Processing ${tireImages.length} tire image(s)...`);

          const results = [];
          const failedImages = [];

          // Process each tire image sequentially to avoid backend overload
          for (const image of tireImages) {
            try {
              console.log(`🔄 Analyzing ${image.position} tire...`);
              const result = await api.analyzeTireWithML(image.file, image.position);
              console.log(`✅ ${image.position} tire analyzed successfully`);
              results.push(result);
            } catch (error) {
              console.error(`❌ Failed to analyze ${image.position} tire:`, error);
              failedImages.push(image.position);
            }
          }

          // Check if we got any successful results
          if (results.length === 0) {
            throw new Error('No tires could be analyzed. Please try again with different images.');
          }

          // Show warning if some images failed
          if (failedImages.length > 0 && results.length > 0) {
            console.warn(`⚠️ ${failedImages.length} tire(s) failed: ${failedImages.join(', ')}`);
            alert(`Partial success: ${results.length} tire(s) analyzed.\n\nFailed: ${failedImages.join(', ')}`);
          }

          console.log(`✅ Successfully analyzed ${results.length} out of ${tireImages.length} tires`);
          setTireResults(results);
        } catch (error) {
          console.error('❌ ML Analysis failed completely:', error);
          alert('ML Analysis failed. Please ensure the backend server is running and try again.');
        } finally {
          setIsAnalyzing(false);
        }
      })();
    }, 2500); // 2.5 second tire rotation for professional feel
  };

  // Step 4: Generate report
  const handleGenerateReport = () => {
    if (!vehicle || tireResults.length === 0) return;

    const overallStatus = determineOverallStatus(tireResults);
    const recommendations = generateRecommendations(tireResults, tireBrand);

    const newInspection = {
      id: `insp-${Date.now()}`,
      createdAt: new Date().toISOString(),
      vehicle,
      tires: tireResults,
      overallStatus,
      recommendations,
      tireBrand: tireBrand || undefined,
    };

    setInspection(newInspection);

    // Save to localStorage via API
    api.saveInspection(newInspection).catch(console.error);
  };

  const handleViewReport = () => {
    if (inspection) {
      navigate(`/inspections/${inspection.id}`);
    }
  };

  const handleNewInspection = () => {
    resetWizard();
    navigate('/inspect');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-brand-50/30 -m-8 p-8">
      {/* Tire Rotation Transition Overlay */}
      {isTransitioning && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-md flex items-center justify-center animate-fade-in">
          <div className="text-center">
            {/* Rotating Tire with Professional Animation */}
            <div className="relative w-40 h-40 mx-auto mb-8">
              {/* Outer glow effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-brand-500 to-purple-600 rounded-full opacity-30 animate-pulse" />

              {/* Main rotating tire */}
              <div className="absolute inset-0 animate-spin-slow">
                <svg className="w-full h-full drop-shadow-2xl" viewBox="0 0 200 200" fill="none">
                  {/* Tire outer ring */}
                  <circle cx="100" cy="100" r="90" fill="url(#tireGradient)" stroke="#1f2937" strokeWidth="4"/>

                  {/* Tire sidewall */}
                  <circle cx="100" cy="100" r="75" fill="#374151" stroke="#1f2937" strokeWidth="2"/>

                  {/* Hub center */}
                  <circle cx="100" cy="100" r="30" fill="url(#hubGradient)" stroke="#6366f1" strokeWidth="3"/>

                  {/* Tread pattern - 8 grooves */}
                  {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, i) => (
                    <g key={i} transform={`rotate(${angle} 100 100)`}>
                      <rect x="95" y="10" width="10" height="25" fill="#1f2937" rx="2"/>
                      <rect x="95" y="165" width="10" height="25" fill="#1f2937" rx="2"/>
                    </g>
                  ))}

                  {/* Spokes */}
                  {[0, 72, 144, 216, 288].map((angle, i) => (
                    <line
                      key={i}
                      x1="100"
                      y1="100"
                      x2={100 + Math.cos((angle * Math.PI) / 180) * 28}
                      y2={100 + Math.sin((angle * Math.PI) / 180) * 28}
                      stroke="url(#spokeGradient)"
                      strokeWidth="3"
                      strokeLinecap="round"
                    />
                  ))}

                  {/* Center cap */}
                  <circle cx="100" cy="100" r="15" fill="#6366f1" opacity="0.8"/>
                  <circle cx="100" cy="100" r="8" fill="#4f46e5"/>

                  {/* Gradients */}
                  <defs>
                    <radialGradient id="tireGradient">
                      <stop offset="0%" stopColor="#4b5563"/>
                      <stop offset="100%" stopColor="#1f2937"/>
                    </radialGradient>
                    <radialGradient id="hubGradient">
                      <stop offset="0%" stopColor="#818cf8"/>
                      <stop offset="100%" stopColor="#4f46e5"/>
                    </radialGradient>
                    <linearGradient id="spokeGradient">
                      <stop offset="0%" stopColor="#6366f1"/>
                      <stop offset="100%" stopColor="#818cf8"/>
                    </linearGradient>
                  </defs>
                </svg>
              </div>

              {/* Inner sparkle effect */}
              <div className="absolute inset-8 bg-gradient-to-r from-brand-400 to-purple-500 rounded-full opacity-20 animate-ping" style={{ animationDuration: '2s' }} />
            </div>

            {/* Professional text */}
            <div className="space-y-3">
              <h3 className="text-2xl font-bold text-white drop-shadow-lg">
                Processing
              </h3>
              <p className="text-gray-300 text-lg font-medium">
                Preparing next phase...
              </p>

              {/* Progress dots */}
              <div className="flex items-center justify-center gap-2 pt-2">
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className="w-2.5 h-2.5 bg-brand-400 rounded-full animate-bounce"
                    style={{ animationDelay: `${i * 0.2}s`, animationDuration: '1s' }}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header with gradient */}
      <div className="relative mb-8">
        <div className="absolute inset-0 bg-gradient-to-r from-brand-600 to-brand-800 rounded-2xl blur-3xl opacity-10" />
        <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-gray-200/50 shadow-xl">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-gradient-to-br from-brand-600 to-brand-700 rounded-2xl flex items-center justify-center shadow-lg shadow-brand-500/50">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="flex-1">
              <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                AI Tire Inspection
              </h1>
              <p className="text-gray-600 mt-1 text-lg">
                Professional tire analysis powered by machine learning
              </p>
            </div>
          </div>
        </div>
      </div>

      <Stepper steps={steps} currentStep={currentStep} />

      {/* Step 1: Vehicle Identification */}
      {currentStep === 0 && (
        <div className="max-w-2xl mx-auto">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-brand-500 to-brand-600 rounded-3xl blur-2xl opacity-20" />
            <div className="relative bg-white/90 backdrop-blur-xl rounded-3xl p-8 shadow-2xl border border-gray-200/50">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-gradient-to-br from-brand-500 to-brand-600 rounded-xl flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-900">
                  Vehicle Identification
                </h2>
              </div>

              <form onSubmit={handleSubmit(handleVehicleSubmit)} className="space-y-6">
                <div>
                  <label
                    htmlFor="vinOrPlate"
                    className="block text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2"
                  >
                    <svg className="w-5 h-5 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    VIN Number (17 Characters)
                  </label>
                  <div className="relative">
                    <input
                      {...register('vinOrPlate')}
                      type="text"
                      id="vinOrPlate"
                      placeholder="e.g., 1HGCM82633A004352"
                      maxLength={17}
                      className="w-full px-6 py-4 text-lg font-mono border-2 border-gray-300 rounded-xl focus:ring-4 focus:ring-brand-500/20 focus:border-brand-500 transition-all bg-white shadow-sm"
                    />
                    <div className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-medium text-gray-400">
                      {(document.getElementById('vinOrPlate') as HTMLInputElement)?.value?.length || 0}/17
                    </div>
                  </div>
                  {errors.vinOrPlate && (
                    <div className="flex items-center gap-2 mt-2 text-sm text-red-600 bg-red-50 p-3 rounded-lg">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                      {errors.vinOrPlate.message}
                    </div>
                  )}
                  {vehicleError && (
                    <div className="flex items-center gap-2 mt-2 text-sm text-red-600 bg-red-50 p-3 rounded-lg">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                      {vehicleError}
                    </div>
                  )}
                  <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    Enter the 17-digit VIN found on your vehicle's dashboard or documents
                  </p>
                </div>

                {/* Tire Brand Selection (Optional) */}
                <div className="border-t border-gray-200 pt-6">
                  <button
                    type="button"
                    onClick={() => setIsTireBrandExpanded(!isTireBrandExpanded)}
                    className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-brand-50/30 rounded-xl hover:from-gray-100 hover:to-brand-50/50 transition-all"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-brand-600 rounded-xl flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                        </svg>
                      </div>
                      <div className="text-left">
                        <div className="font-semibold text-gray-900">Tire Brand</div>
                        <div className="text-sm text-gray-500">
                          {tireBrand ? tireBrand : 'Optional - for smarter recommendations'}
                        </div>
                      </div>
                    </div>
                    <svg
                      className={`w-5 h-5 text-gray-500 transition-transform ${isTireBrandExpanded ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {isTireBrandExpanded && (
                    <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {TIRE_BRANDS.map((brand) => (
                        <button
                          key={brand}
                          type="button"
                          onClick={() => setTireBrand(tireBrand === brand ? null : brand)}
                          className={`px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                            tireBrand === brand
                              ? 'bg-gradient-to-r from-brand-600 to-purple-600 text-white shadow-lg shadow-brand-500/30'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {brand}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex gap-3 pt-4">
                  <Button type="submit" isLoading={isLoadingVehicle} size="lg" className="flex-1 shadow-lg">
                    {isLoadingVehicle ? 'Decoding VIN...' : 'Continue to Photos'}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="lg"
                    onClick={() => navigate('/')}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Step 2: Tire Photos */}
      {currentStep === 1 && vehicle && (
        <div className="space-y-6">
          <VehicleBanner vehicle={vehicle} />

          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-brand-500 to-purple-600 rounded-3xl blur-3xl opacity-10" />
            <div className="relative bg-white/90 backdrop-blur-xl rounded-3xl p-8 shadow-2xl border border-gray-200/50">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-brand-600 rounded-xl flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">
                      Upload Tire Photos
                    </h2>
                    <p className="text-sm text-gray-600 mt-1">
                      Click on each tire position to upload a photo
                    </p>
                  </div>
                </div>
                <div className="hidden sm:flex items-center gap-2 bg-brand-50 px-4 py-2 rounded-xl">
                  <svg className="w-5 h-5 text-brand-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm font-medium text-brand-700">
                    {tireImages.length}/4 uploaded
                  </span>
                </div>
              </div>

              {/* Interactive Car Visualization - Top-down view */}
              <div className="relative w-full h-[540px] flex items-center justify-center mb-8 py-8 bg-slate-50 rounded-3xl">
                {/* Background glow effect */}
                <div className="absolute w-72 h-[420px] bg-blue-200 rounded-full opacity-20 blur-3xl" />

                {/* Car Body - Top-down perspective */}
                <div className="relative w-64 h-[420px] shadow-2xl rounded-[50px] overflow-hidden z-10">
                  {/* Front bumper with headlights */}
                  <div className="absolute top-0 left-0 right-0 h-10 bg-gradient-to-b from-blue-400 to-blue-600 rounded-t-[50px] flex items-center justify-center z-0">
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
                    {/* Headlights */}
                    <div className="absolute top-2 flex justify-between w-3/4">
                      <div className="w-5 h-3 bg-yellow-200 rounded-md shadow-lg shadow-yellow-200/70" />
                      <div className="w-5 h-3 bg-yellow-200 rounded-md shadow-lg shadow-yellow-200/70" />
                    </div>
                  </div>

                  {/* Hood area with shine */}
                  <div className="absolute top-10 left-0 right-0 h-24 bg-gradient-to-b from-blue-300 via-blue-400 to-blue-600 flex flex-col items-center justify-center z-0">
                    {/* Hood shine */}
                    <div className="absolute top-3 w-32 h-8 bg-white/20 rounded-full" />
                    {/* Windshield stripe */}
                    <div className="w-40 h-1 bg-blue-900/40 rounded-full mt-3" />
                  </div>

                  {/* Main cabin with windows */}
                  <div className="absolute top-34 left-0 right-0 h-36 bg-gradient-to-r from-blue-900 via-blue-800 to-blue-900 flex items-center justify-center border-l-2 border-r-2 border-black/15 z-0" style={{ top: '136px' }}>
                    {/* Side windows */}
                    <div className="flex gap-6">
                      <div className="w-16 h-10 bg-blue-400/30 rounded-lg border border-blue-500/30" />
                      <div className="w-16 h-10 bg-blue-400/30 rounded-lg border border-blue-500/30" />
                    </div>
                  </div>

                  {/* Trunk area with shine */}
                  <div className="absolute left-0 right-0 h-24 bg-gradient-to-b from-blue-600 via-blue-400 to-blue-300 flex items-center justify-center z-0" style={{ top: '280px' }}>
                    <div className="w-32 h-8 bg-white/15 rounded-full -mt-2" />
                  </div>

                  {/* Rear bumper with tail lights */}
                  <div className="absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-b from-blue-600 to-blue-700 rounded-b-[50px] flex items-center justify-center z-0">
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent" />
                    {/* Tail lights */}
                    <div className="absolute bottom-2 flex justify-between w-3/4">
                      <div className="w-6 h-3 bg-gradient-to-b from-red-500 to-red-600 rounded-md shadow-lg shadow-red-500/70" />
                      <div className="w-6 h-3 bg-gradient-to-b from-red-500 to-red-600 rounded-md shadow-lg shadow-red-500/70" />
                    </div>
                  </div>

                  {/* Center line detail */}
                  <div className="absolute left-1/2 top-10 bottom-10 w-0.5 bg-white/10 -ml-px" />

                  {/* Front Left Tire */}
                  <button
                    onClick={() => {
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.accept = 'image/*';
                      input.onchange = (e) => {
                        const file = (e.target as HTMLInputElement).files?.[0];
                        if (file) handleTireUpload('FL', file);
                      };
                      input.click();
                    }}
                    className="absolute -left-3 top-24 z-20 group"
                  >
                    <div className={`w-28 h-28 rounded-full border-[8px] ${
                      tireImages.find(img => img.position === 'FL')
                        ? 'border-green-500 bg-green-950 shadow-lg shadow-green-500/60'
                        : 'border-zinc-700 bg-zinc-950'
                    } flex items-center justify-center shadow-2xl transition-all hover:scale-110 hover:shadow-3xl relative`}>
                      {tireImages.find(img => img.position === 'FL') ? (
                        <>
                          <img
                            src={tireImages.find(img => img.position === 'FL')?.preview}
                            alt="FL Tire"
                            className="w-full h-full object-cover rounded-full"
                          />
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              removeTireImage('FL');
                            }}
                            className="absolute -top-2 -right-2 w-7 h-7 bg-red-500 text-white rounded-full flex items-center justify-center shadow-lg hover:bg-red-600 transition-colors"
                          >
                            ✕
                          </button>
                          <div className="absolute -top-2 -left-2 w-7 h-7 bg-green-500 text-white rounded-full flex items-center justify-center shadow-lg">
                            ✓
                          </div>
                        </>
                      ) : (
                        <div className="flex flex-col items-center">
                          {/* Inner rim */}
                          <div className="w-16 h-16 rounded-full bg-zinc-700 border-[3px] border-zinc-600 flex items-center justify-center mb-1 shadow-inner">
                            <svg className="w-9 h-9 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                          </div>
                          <span className="text-[9px] font-black text-zinc-400 tracking-widest">FL</span>
                        </div>
                      )}
                    </div>
                  </button>

                  {/* Front Right Tire */}
                  <button
                    onClick={() => {
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.accept = 'image/*';
                      input.onchange = (e) => {
                        const file = (e.target as HTMLInputElement).files?.[0];
                        if (file) handleTireUpload('FR', file);
                      };
                      input.click();
                    }}
                    className="absolute -right-3 top-24 z-20 group"
                  >
                    <div className={`w-28 h-28 rounded-full border-[8px] ${
                      tireImages.find(img => img.position === 'FR')
                        ? 'border-green-500 bg-green-950 shadow-lg shadow-green-500/60'
                        : 'border-zinc-700 bg-zinc-950'
                    } flex items-center justify-center shadow-2xl transition-all hover:scale-110 hover:shadow-3xl relative`}>
                      {tireImages.find(img => img.position === 'FR') ? (
                        <>
                          <img
                            src={tireImages.find(img => img.position === 'FR')?.preview}
                            alt="FR Tire"
                            className="w-full h-full object-cover rounded-full"
                          />
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              removeTireImage('FR');
                            }}
                            className="absolute -top-2 -right-2 w-7 h-7 bg-red-500 text-white rounded-full flex items-center justify-center shadow-lg hover:bg-red-600 transition-colors"
                          >
                            ✕
                          </button>
                          <div className="absolute -top-2 -left-2 w-7 h-7 bg-green-500 text-white rounded-full flex items-center justify-center shadow-lg">
                            ✓
                          </div>
                        </>
                      ) : (
                        <div className="flex flex-col items-center">
                          <div className="w-16 h-16 rounded-full bg-zinc-700 border-[3px] border-zinc-600 flex items-center justify-center mb-1 shadow-inner">
                            <svg className="w-9 h-9 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                          </div>
                          <span className="text-[9px] font-black text-zinc-400 tracking-widest">FR</span>
                        </div>
                      )}
                    </div>
                  </button>

                  {/* Rear Left Tire */}
                  <button
                    onClick={() => {
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.accept = 'image/*';
                      input.onchange = (e) => {
                        const file = (e.target as HTMLInputElement).files?.[0];
                        if (file) handleTireUpload('RL', file);
                      };
                      input.click();
                    }}
                    className="absolute -left-3 bottom-20 z-20 group"
                  >
                    <div className={`w-28 h-28 rounded-full border-[8px] ${
                      tireImages.find(img => img.position === 'RL')
                        ? 'border-green-500 bg-green-950 shadow-lg shadow-green-500/60'
                        : 'border-zinc-700 bg-zinc-950'
                    } flex items-center justify-center shadow-2xl transition-all hover:scale-110 hover:shadow-3xl relative`}>
                      {tireImages.find(img => img.position === 'RL') ? (
                        <>
                          <img
                            src={tireImages.find(img => img.position === 'RL')?.preview}
                            alt="RL Tire"
                            className="w-full h-full object-cover rounded-full"
                          />
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              removeTireImage('RL');
                            }}
                            className="absolute -top-2 -right-2 w-7 h-7 bg-red-500 text-white rounded-full flex items-center justify-center shadow-lg hover:bg-red-600 transition-colors"
                          >
                            ✕
                          </button>
                          <div className="absolute -top-2 -left-2 w-7 h-7 bg-green-500 text-white rounded-full flex items-center justify-center shadow-lg">
                            ✓
                          </div>
                        </>
                      ) : (
                        <div className="flex flex-col items-center">
                          <div className="w-16 h-16 rounded-full bg-zinc-700 border-[3px] border-zinc-600 flex items-center justify-center mb-1 shadow-inner">
                            <svg className="w-9 h-9 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                          </div>
                          <span className="text-[9px] font-black text-zinc-400 tracking-widest">RL</span>
                        </div>
                      )}
                    </div>
                  </button>

                  {/* Rear Right Tire */}
                  <button
                    onClick={() => {
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.accept = 'image/*';
                      input.onchange = (e) => {
                        const file = (e.target as HTMLInputElement).files?.[0];
                        if (file) handleTireUpload('RR', file);
                      };
                      input.click();
                    }}
                    className="absolute -right-3 bottom-20 z-20 group"
                  >
                    <div className={`w-28 h-28 rounded-full border-[8px] ${
                      tireImages.find(img => img.position === 'RR')
                        ? 'border-green-500 bg-green-950 shadow-lg shadow-green-500/60'
                        : 'border-zinc-700 bg-zinc-950'
                    } flex items-center justify-center shadow-2xl transition-all hover:scale-110 hover:shadow-3xl relative`}>
                      {tireImages.find(img => img.position === 'RR') ? (
                        <>
                          <img
                            src={tireImages.find(img => img.position === 'RR')?.preview}
                            alt="RR Tire"
                            className="w-full h-full object-cover rounded-full"
                          />
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              removeTireImage('RR');
                            }}
                            className="absolute -top-2 -right-2 w-7 h-7 bg-red-500 text-white rounded-full flex items-center justify-center shadow-lg hover:bg-red-600 transition-colors"
                          >
                            ✕
                          </button>
                          <div className="absolute -top-2 -left-2 w-7 h-7 bg-green-500 text-white rounded-full flex items-center justify-center shadow-lg">
                            ✓
                          </div>
                        </>
                      ) : (
                        <div className="flex flex-col items-center">
                          <div className="w-16 h-16 rounded-full bg-zinc-700 border-[3px] border-zinc-600 flex items-center justify-center mb-1 shadow-inner">
                            <svg className="w-9 h-9 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                          </div>
                          <span className="text-[9px] font-black text-zinc-400 tracking-widest">RR</span>
                        </div>
                      )}
                    </div>
                  </button>
                </div>
              </div>

              <div className="bg-gradient-to-r from-brand-50 to-purple-50 rounded-2xl p-6 mb-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm">
                    <svg className="w-5 h-5 text-brand-600" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M11 3a1 1 0 10-2 0v1a1 1 0 102 0V3zM15.657 5.757a1 1 0 00-1.414-1.414l-.707.707a1 1 0 001.414 1.414l.707-.707zM18 10a1 1 0 01-1 1h-1a1 1 0 110-2h1a1 1 0 011 1zM5.05 6.464A1 1 0 106.464 5.05l-.707-.707a1 1 0 00-1.414 1.414l.707.707zM5 10a1 1 0 01-1 1H3a1 1 0 110-2h1a1 1 0 011 1zM8 16v-1h4v1a2 2 0 11-4 0zM12 14c.015-.34.208-.646.477-.859a4 4 0 10-4.954 0c.27.213.462.519.476.859h4.002z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 mb-1">Pro Tips for Best Results</h3>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-brand-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        Ensure good lighting and clear view of tire tread
                      </li>
                      <li className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-brand-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        Capture straight-on photos for accurate analysis
                      </li>
                      <li className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-brand-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        Include DOT code if visible for age verification
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={handleAnalyze}
                  disabled={!canProceedToAnalysis}
                  size="lg"
                  className="flex-1 shadow-lg bg-gradient-to-r from-brand-600 to-brand-700 hover:from-brand-700 hover:to-brand-800"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Analyze with AI
                </Button>
                <Button variant="ghost" size="lg" onClick={previousStep}>
                  Back
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Step 3: ML Analysis in Progress */}
      {currentStep === 2 && (
        <div className="max-w-3xl mx-auto">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-brand-500 via-purple-500 to-pink-500 rounded-3xl blur-3xl opacity-20 animate-pulse" />
            <div className="relative bg-white/95 backdrop-blur-xl rounded-3xl p-12 shadow-2xl border border-gray-200/50">
              <div className="text-center">
                {isAnalyzing ? (
                  <>
                    <div className="relative w-32 h-32 mx-auto mb-8">
                      <div className="absolute inset-0 bg-gradient-to-r from-brand-500 to-purple-600 rounded-full opacity-20 animate-ping" />
                      <div className="absolute inset-0 bg-gradient-to-r from-brand-600 to-purple-700 rounded-full animate-spin" style={{ clipPath: 'polygon(50% 50%, 50% 0%, 100% 0%, 100% 50%)' }} />
                      <div className="absolute inset-2 bg-white rounded-full flex items-center justify-center">
                        <svg className="w-16 h-16 text-brand-600 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                        </svg>
                      </div>
                    </div>
                    <h2 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-3">
                      AI-Powered Analysis in Progress
                    </h2>
                    <p className="text-gray-600 mb-4 text-lg">
                      Processing {tireImages.length} tire image{tireImages.length > 1 ? 's' : ''} with machine learning
                    </p>

                    {/* Progress Bar */}
                    <div className="max-w-md mx-auto mb-6">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-brand-700">
                          Progress: {Math.round((currentAnalysisStep / analysisSteps.length) * 100)}%
                        </span>
                        <span className="text-xs text-gray-500">
                          Step {currentAnalysisStep + 1} of {analysisSteps.length}
                        </span>
                      </div>
                      <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-brand-500 to-purple-600 transition-all duration-500 ease-out"
                          style={{ width: `${(currentAnalysisStep / analysisSteps.length) * 100}%` }}
                        />
                      </div>
                    </div>

                    <div className="max-w-md mx-auto space-y-2 mb-8">
                      {analysisSteps.map((step, idx) => {
                        const isCompleted = idx < currentAnalysisStep;
                        const isCurrent = idx === currentAnalysisStep;

                        return (
                          <div
                            key={idx}
                            className={`flex items-center gap-3 p-3 rounded-xl transition-all duration-300 ${
                              isCurrent
                                ? 'bg-gradient-to-r from-brand-50 to-purple-50 border-2 border-brand-300 scale-105 shadow-md'
                                : isCompleted
                                ? 'bg-green-50 border border-green-200'
                                : 'bg-gray-50 border border-gray-200 opacity-50'
                            }`}
                          >
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
                              isCurrent
                                ? 'bg-gradient-to-r from-brand-500 to-purple-600 shadow-lg shadow-brand-500/50'
                                : isCompleted
                                ? 'bg-green-500'
                                : 'bg-gray-300'
                            }`}>
                              {isCurrent ? (
                                <svg className="w-5 h-5 text-white animate-spin" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                              ) : isCompleted ? (
                                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              ) : (
                                <div className="w-2 h-2 bg-white rounded-full opacity-50" />
                              )}
                            </div>
                            <div className="flex-1 flex items-center gap-2">
                              <span className={`text-sm font-medium transition-all duration-300 ${
                                isCurrent ? 'text-brand-700' : isCompleted ? 'text-green-700' : 'text-gray-500'
                              }`}>
                                {step.label}
                              </span>
                              {isCurrent && (
                                <span className="text-lg animate-pulse">{step.icon}</span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div className="inline-flex items-center gap-2 bg-gradient-to-r from-brand-50 to-purple-50 px-6 py-3 rounded-xl border border-brand-200">
                      <svg className="w-5 h-5 text-brand-600" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M13 7H7v6h6V7z" />
                        <path fillRule="evenodd" d="M7 2a1 1 0 012 0v1h2V2a1 1 0 112 0v1h2a2 2 0 012 2v2h1a1 1 0 110 2h-1v2h1a1 1 0 110 2h-1v2a2 2 0 01-2 2h-2v1a1 1 0 11-2 0v-1H9v1a1 1 0 11-2 0v-1H5a2 2 0 01-2-2v-2H2a1 1 0 110-2h1V9H2a1 1 0 010-2h1V5a2 2 0 012-2h2V2zM5 5h10v10H5V5z" clipRule="evenodd" />
                      </svg>
                      <span className="text-sm font-semibold text-brand-700">
                        OpenCV + TensorFlow Neural Network
                      </span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="relative w-24 h-24 mx-auto mb-8">
                      <div className="absolute inset-0 bg-green-400 rounded-full opacity-20 animate-ping" />
                      <div className="absolute inset-0 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full flex items-center justify-center shadow-2xl shadow-green-500/50">
                        <svg
                          className="w-14 h-14 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={3}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </div>
                    </div>
                    <h2 className="text-3xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent mb-3">
                      Analysis Complete!
                    </h2>
                    <p className="text-gray-600 mb-8 text-lg">
                      All tires have been successfully analyzed
                    </p>
                    <Button onClick={nextStep} size="lg" className="shadow-xl">
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                      View Detailed Results
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Step 4: Summary & Results */}
      {currentStep === 3 && vehicle && tireResults.length > 0 && (
        <div className="space-y-6">
          <VehicleBanner vehicle={vehicle} />

          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 to-brand-600 rounded-3xl blur-3xl opacity-10" />
            <div className="relative bg-white/90 backdrop-blur-xl rounded-3xl p-8 shadow-2xl border border-gray-200/50">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/50">
                    <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">
                      Inspection Results
                    </h2>
                    <p className="text-sm text-gray-600">
                      AI analysis completed for {tireResults.length} tire{tireResults.length > 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
                <div className="hidden sm:block bg-gradient-to-r from-emerald-50 to-brand-50 px-4 py-2 rounded-xl border border-emerald-200">
                  <div className="text-xs text-gray-600 font-medium">Overall Status</div>
                  <div className="text-lg font-bold bg-gradient-to-r from-emerald-600 to-brand-600 bg-clip-text text-transparent">
                    {determineOverallStatus(tireResults).toUpperCase()}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {tireResults.map((tire) => (
                  <TireResultCard key={tire.position} tire={tire} />
                ))}
              </div>
            </div>
          </div>

          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-3xl blur-3xl opacity-10" />
            <div className="relative bg-white/90 backdrop-blur-xl rounded-3xl p-8 shadow-2xl border border-gray-200/50">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900">
                  Professional Recommendations
                </h3>
              </div>

              <div className="space-y-3">
                {generateRecommendations(tireResults, tireBrand).map((rec, idx) => (
                  <div key={idx} className="flex items-start gap-3 bg-gradient-to-r from-gray-50 to-blue-50/30 p-4 rounded-xl border border-gray-200 hover:border-brand-300 transition-colors">
                    <div className="w-6 h-6 bg-gradient-to-r from-brand-500 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span className="text-gray-800 font-medium flex-1">{rec}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            {!inspection ? (
              <Button onClick={handleGenerateReport} size="lg" className="flex-1 shadow-xl bg-gradient-to-r from-brand-600 to-brand-700 hover:from-brand-700 hover:to-brand-800">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Generate Full Report
              </Button>
            ) : (
              <>
                <Button onClick={handleViewReport} size="lg" className="flex-1 shadow-lg">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  View Full Report
                </Button>
                <Button variant="outline" size="lg" onClick={handleNewInspection}>
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  New Inspection
                </Button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
